import {
  BoxProps,
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { SupplierProfileDto, useLinkSupplierProfileApi, useSearchAvailableSuppliersApi } from 'apis/supplier-profiles';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: number;
  label: string;
};

type TInputs = {
  supplier: TOption | null;
};

const defaultValues: TInputs = {
  supplier: null,
};

export interface ILinkSupplierModalCell extends BoxProps {
  trigger: JSX.Element;
  profile: SupplierProfileDto;
}

export const LinkSupplierModal = ({ trigger, profile }: ILinkSupplierModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { loadOptions } = useSearchAvailableSuppliersApi();
  const { mutate: linkSupplierProfile, isLoading } = useLinkSupplierProfileApi();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  const handleClose = () => {
    reset({ ...defaultValues });
    onClose();
  };

  const onSubmit = (values: TInputs) => {
    if (!values.supplier) return;

    linkSupplierProfile(
      {
        id: profile.id,
        supplierId: values.supplier.value,
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: 'md' }}>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Link Supplier</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing='md'>
              <Text fontSize='sm' color='gray.500'>
                Link the profile <strong>{profile.businessName}</strong> to an existing supplier. Only suppliers without
                a profile are offered.
              </Text>

              <FormControl isRequired isInvalid={!!errors.supplier}>
                <FormLabel>Supplier</FormLabel>
                <Controller
                  control={control}
                  name='supplier'
                  rules={{ required: 'Supplier is required' }}
                  render={({ field }) => (
                    <ChakraAsyncSelect
                      {...field}
                      placeholder='Search suppliers...'
                      loadOptions={loadOptions}
                      isClearable
                    />
                  )}
                />
                <FormErrorMessage>{(errors.supplier as any)?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button variant='outline' colorScheme='red' onClick={handleClose}>
                Cancel
              </Button>
              <Button variant='solid' colorScheme='red' type='submit' isLoading={isLoading}>
                Link
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
